import { FC, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  Container,
  IconButton,
  InputAdornment,
  MenuItem,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  AddOutlined,
  Close,
  Inventory2Outlined,
  LocalOfferOutlined,
  RestartAltOutlined,
  SearchOutlined,
  TuneOutlined,
  WarehouseOutlined,
} from "@mui/icons-material";
import useSWR from "swr";

import {
  AxiosError,
  ProductWarehouse,
  TBrand,
  TCategory,
  TProduct,
  TRestProductsV4,
  TSearchProductV4,
  getBrands,
  getCategories,
  searchProductsES,
  updateProductV2,
} from "@/api";
import { useNotify } from "react-admin";
import { country } from "@/config/country";
import { PRODUCTS } from "@/config/resources";
import { useAdmin } from "@/hooks/admin";
import useDebounce from "@/hooks/debounce";

type StockFilter = "all" | "true" | "false";
type StatusFilter = "all" | "true" | "false";
type ProductType = "PRODUCT" | "KIT" | "GROUP";

const ALL_TYPES: ProductType[] = ["PRODUCT", "KIT", "GROUP"];
const PAGE_SIZE = 25;

const formatPrice = (n: number) =>
  new Intl.NumberFormat(country.locale ?? "es-CO", {
    style: "currency",
    currency: country.currency ?? "COP",
    maximumFractionDigits: 0,
  }).format(n || 0);

export const ProductListV2 = () => {
  const { hasPermission } = useAdmin();
  const navigate = useNavigate();

  const [text, setText] = useState("");
  const [stock, setStock] = useState<StockFilter>("all");
  const [status, setStatus] = useState<StatusFilter>("all");
  const [brandIds, setBrandIds] = useState<string[]>([]);
  const [categoryIds, setCategoryIds] = useState<string[]>([]);
  const [types, setTypes] = useState<ProductType[]>(["PRODUCT"]);
  const [page, setPage] = useState(0);

  const debouncedText = useDebounce(text, 300);

  useEffect(() => setPage(0), [
    debouncedText,
    stock,
    status,
    brandIds.join(","),
    categoryIds.join(","),
    types.join(","),
  ]);

  const payload = useMemo(
    () => ({
      text: debouncedText || undefined,
      countryCode: country.code,
      hasStock: stock === "all" ? null : stock === "true",
      status: status === "all" ? undefined : status === "true",
      brandId: brandIds,
      categoryId: categoryIds,
      type: types,
      page,
      size: PAGE_SIZE,
      sort: { field: "name", sort: "asc" },
    }),
    [
      debouncedText,
      stock,
      status,
      brandIds.join(","),
      categoryIds.join(","),
      types.join(","),
      page,
    ]
  );

  const { data, isValidating } = useSWR<TRestProductsV4>(
    ["products-v2-search", payload],
    () => searchProductsES(payload as any),
    { revalidateOnFocus: false, keepPreviousData: true }
  );

  if (!hasPermission("product.list")) return null;

  const products = data?.content ?? [];
  const total = data?.totalElements ?? 0;
  const totalPages = data?.totalPages ?? 1;

  const resetFilters = () => {
    setText("");
    setStock("all");
    setStatus("all");
    setBrandIds([]);
    setCategoryIds([]);
    setTypes(["PRODUCT"]);
  };

  const activeFilterCount =
    (stock !== "all" ? 1 : 0) +
    (status !== "all" ? 1 : 0) +
    brandIds.length +
    categoryIds.length +
    (types.length === 1 && types[0] === "PRODUCT" ? 0 : 1);

  return (
    <Container maxWidth="xl" disableGutters sx={{ py: { xs: 1.5, md: 2 } }}>
      {/* Header */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems={{ md: "center" }}
        justifyContent="space-between"
        spacing={1.5}
        sx={{ mb: 2 }}
      >
        <Stack spacing={0.25} sx={{ minWidth: 0 }}>
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ letterSpacing: "0.14em", lineHeight: 1 }}
          >
            Catálogo · v2
          </Typography>
          <Stack direction="row" spacing={1.5} alignItems="baseline">
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: { xs: "1.5rem", md: "1.75rem" },
                lineHeight: 1.1,
              }}
            >
              Productos
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isValidating && !data
                ? "…"
                : `${total.toLocaleString(country.locale ?? "es-CO")}`}
            </Typography>
          </Stack>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
          size="small"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Buscar por nombre o SKU"
          sx={{ width: { xs: "100%", md: 320 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlined fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: text ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setText("")}>
                  <Close fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
          {hasPermission("product.create") && (
            <Button
              variant="contained"
              startIcon={<AddOutlined />}
              onClick={() => navigate(`/${PRODUCTS}/create`)}
              sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
            >
              Crear producto
            </Button>
          )}
        </Stack>
      </Stack>

      {/* Filters */}
      <Card variant="outlined" sx={{ borderRadius: 1.5, mb: 2 }}>
        <Box
          sx={{
            p: { xs: 1, md: 1.5 },
            display: "grid",
            gap: 1,
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              md: "repeat(5, minmax(0, 1fr))",
            },
            alignItems: "center",
          }}
        >
          <SelectFilter
            label="Stock"
            value={stock}
            onChange={(v) => setStock(v as StockFilter)}
            options={[
              { value: "all", label: "Todo el stock" },
              { value: "true", label: "Con stock" },
              { value: "false", label: "Sin stock" },
            ]}
          />
          <SelectFilter
            label="Estado"
            value={status}
            onChange={(v) => setStatus(v as StatusFilter)}
            options={[
              { value: "all", label: "Todos" },
              { value: "true", label: "Activos" },
              { value: "false", label: "Inactivos" },
            ]}
          />
          <CategoryAutocomplete value={categoryIds} onChange={setCategoryIds} />
          <BrandAutocomplete value={brandIds} onChange={setBrandIds} />
          <TypeMultiselect value={types} onChange={setTypes} />
        </Box>
        {activeFilterCount > 0 && (
          <Box
            sx={{
              borderTop: 1,
              borderColor: "divider",
              px: { xs: 1, md: 1.5 },
              py: 0.5,
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <TuneOutlined fontSize="small" color="action" />
            <Typography variant="caption" color="text.secondary">
              {activeFilterCount} filtro{activeFilterCount === 1 ? "" : "s"}{" "}
              activo{activeFilterCount === 1 ? "" : "s"}
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Button
              size="small"
              startIcon={<RestartAltOutlined fontSize="small" />}
              onClick={resetFilters}
            >
              Limpiar
            </Button>
          </Box>
        )}
      </Card>

      {/* Table */}
      <Card variant="outlined" sx={{ borderRadius: 1.5 }}>
        <TableContainer>
          <Table size="small" sx={{ minWidth: 880 }}>
            <TableHead>
              <TableRow
                sx={{
                  "& th": {
                    fontWeight: 700,
                    textTransform: "uppercase",
                    fontSize: "0.7rem",
                    letterSpacing: "0.06em",
                    color: "text.secondary",
                    bgcolor: (t) =>
                      t.palette.mode === "dark"
                        ? "background.paper"
                        : "grey.50",
                  },
                }}
              >
                <TableCell sx={{ width: 56 }}></TableCell>
                <TableCell>Producto</TableCell>
                <TableCell>Marca</TableCell>
                <TableCell>Categoría</TableCell>
                <TableCell align="right">Precio</TableCell>
                <TableCell align="right">Stock</TableCell>
                <TableCell align="center" sx={{ width: 64 }}>
                  Bodegas
                </TableCell>
                <TableCell align="center" sx={{ width: 96 }}>
                  Estado
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!data ? (
                Array.from({ length: 10 }).map((_, i) => (
                  <RowSkeleton key={i} />
                ))
              ) : products.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8}>
                    <EmptyState
                      hasFilters={activeFilterCount > 0}
                      onReset={resetFilters}
                    />
                  </TableCell>
                </TableRow>
              ) : (
                products.map((p) => (
                  <ProductRow
                    key={p.id}
                    product={p}
                    onClick={() => navigate(`/${PRODUCTS}/${p.id}/show`)}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {data && totalPages > 1 && (
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              px: { xs: 1.5, md: 2 },
              py: 1,
              borderTop: 1,
              borderColor: "divider",
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Página {page + 1} de {totalPages} · {total.toLocaleString()} en
              total
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="outlined"
                disabled={page <= 0 || isValidating}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                Anterior
              </Button>
              <Button
                size="small"
                variant="outlined"
                disabled={page + 1 >= totalPages || isValidating}
                onClick={() => setPage((p) => p + 1)}
              >
                Siguiente
              </Button>
            </Stack>
          </Stack>
        )}
      </Card>
    </Container>
  );
};

const ProductRow: FC<{ product: TSearchProductV4; onClick: () => void }> = ({
  product,
  onClick,
}) => {
  const theme = useTheme();
  const totalStock =
    product.availableWarehouses?.reduce(
      (acc: number, w: ProductWarehouse) => acc + (w.availableStock ?? 0),
      0
    ) ?? 0;
  const warehouseCount = product.availableWarehouses?.length ?? 0;
  const img = product.media?.[0]?.src ?? product.images?.[0];
  const hasStock = totalStock > 0;
  const inactive = product.status === false;

  return (
    <TableRow
      hover
      onClick={onClick}
      sx={{
        cursor: "pointer",
        opacity: inactive ? 0.6 : 1,
        "& td": {
          borderColor: "divider",
        },
      }}
    >
      <TableCell>
        <Avatar
          src={img}
          alt={product.name}
          variant="rounded"
          sx={{
            width: 40,
            height: 40,
            bgcolor: "action.hover",
            color: "text.disabled",
          }}
        >
          <Inventory2Outlined fontSize="small" />
        </Avatar>
      </TableCell>
      <TableCell sx={{ minWidth: 220 }}>
        <Stack spacing={0.25}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              lineHeight: 1.25,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: 360,
            }}
            title={product.name}
          >
            {product.name}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontFamily: "monospace", fontSize: "0.7rem" }}
          >
            {product.sku}
          </Typography>
        </Stack>
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="text.secondary">
          {product.brand?.name ?? "—"}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="text.secondary">
          {product.category?.name ?? "—"}
        </Typography>
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {formatPrice(product.wholesalePrice ?? product.retailPrice ?? 0)}
        </Typography>
      </TableCell>
      <TableCell align="right">
        <Typography
          variant="body2"
          sx={{
            fontWeight: 700,
            color: hasStock
              ? theme.palette.success.main
              : theme.palette.error.main,
          }}
        >
          {totalStock.toLocaleString(country.locale ?? "es-CO")}
        </Typography>
      </TableCell>
      <TableCell align="center">
        <Tooltip
          title={`${warehouseCount} bodega${warehouseCount === 1 ? "" : "s"}`}
        >
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            justifyContent="center"
            sx={{ color: "text.secondary" }}
          >
            <WarehouseOutlined sx={{ fontSize: 14 }} />
            <Typography variant="caption">{warehouseCount}</Typography>
          </Stack>
        </Tooltip>
      </TableCell>
      <TableCell align="center">
        <StatusToggleCell product={product} />
      </TableCell>
    </TableRow>
  );
};

const StatusToggleCell: FC<{ product: TSearchProductV4 }> = ({ product }) => {
  const notify = useNotify();
  const { hasPermission } = useAdmin();
  const canEdit = hasPermission("product.update");
  const [optimistic, setOptimistic] = useState<boolean | null>(null);
  const [saving, setSaving] = useState(false);

  const current = optimistic ?? product.status !== false;
  const label = current ? "Activo" : "Inactivo";

  const toggle = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!canEdit || saving) return;
    const next = !current;
    setOptimistic(next);
    setSaving(true);
    try {
      await updateProductV2(product.id, {
        status: next,
        supplierId: product.supplierId,
      } as Partial<TProduct>);
      notify(next ? "Producto activado" : "Producto desactivado", {
        type: "success",
      });
    } catch (e) {
      const msg =
        e instanceof AxiosError ? e.response?.data?.message : String(e);
      notify(msg ?? "Error al cambiar el estado", { type: "error" });
      setOptimistic(null);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Chip
      label={label}
      size="small"
      color={current ? "success" : "default"}
      variant="outlined"
      onClick={canEdit ? toggle : undefined}
      disabled={saving}
      sx={{
        fontWeight: 600,
        fontSize: "0.7rem",
        height: 20,
        cursor: canEdit ? "pointer" : "default",
      }}
    />
  );
};

const RowSkeleton = () => (
  <TableRow>
    <TableCell>
      <Skeleton variant="rounded" width={40} height={40} />
    </TableCell>
    <TableCell>
      <Skeleton width="70%" />
      <Skeleton width="30%" />
    </TableCell>
    <TableCell>
      <Skeleton width={80} />
    </TableCell>
    <TableCell>
      <Skeleton width={80} />
    </TableCell>
    <TableCell align="right">
      <Skeleton width={60} sx={{ ml: "auto" }} />
    </TableCell>
    <TableCell align="right">
      <Skeleton width={40} sx={{ ml: "auto" }} />
    </TableCell>
    <TableCell align="center">
      <Skeleton width={20} sx={{ mx: "auto" }} />
    </TableCell>
    <TableCell align="center">
      <Skeleton width={60} sx={{ mx: "auto" }} />
    </TableCell>
  </TableRow>
);

const EmptyState: FC<{ hasFilters: boolean; onReset: () => void }> = ({
  hasFilters,
  onReset,
}) => (
  <Stack alignItems="center" sx={{ py: 6 }} spacing={1.5}>
    <Avatar
      sx={{
        bgcolor: "action.hover",
        color: "text.disabled",
        width: 48,
        height: 48,
      }}
    >
      <Inventory2Outlined />
    </Avatar>
    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
      Sin resultados
    </Typography>
    <Typography variant="body2" color="text.secondary">
      {hasFilters
        ? "Probá quitar algunos filtros o cambiar la búsqueda."
        : "No hay productos por mostrar todavía."}
    </Typography>
    {hasFilters && (
      <Button
        size="small"
        startIcon={<RestartAltOutlined fontSize="small" />}
        variant="outlined"
        onClick={onReset}
      >
        Limpiar filtros
      </Button>
    )}
  </Stack>
);

const SelectFilter: FC<{
  icon?: React.ReactNode;
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
}> = ({ label, value, options, onChange }) => (
  <TextField
    select
    size="small"
    label={label}
    value={value}
    onChange={(e) => onChange(e.target.value)}
  >
    {options.map((o) => (
      <MenuItem key={o.value} value={o.value}>
        {o.label}
      </MenuItem>
    ))}
  </TextField>
);

const TypeMultiselect: FC<{
  value: ProductType[];
  onChange: (v: ProductType[]) => void;
}> = ({ value, onChange }) => {
  const labels: Record<ProductType, string> = {
    PRODUCT: "Producto",
    KIT: "Kit",
    GROUP: "Grupo",
  };
  return (
    <Stack
      direction="row"
      spacing={0.5}
      sx={{
        flexWrap: "wrap",
        gap: 0.5,
        alignItems: "center",
        height: "100%",
      }}
    >
      {ALL_TYPES.map((t) => {
        const active = value.includes(t);
        return (
          <Chip
            key={t}
            icon={<LocalOfferOutlined sx={{ fontSize: 14 }} />}
            label={labels[t]}
            size="small"
            variant={active ? "filled" : "outlined"}
            color={active ? "primary" : "default"}
            onClick={() =>
              onChange(active ? value.filter((x) => x !== t) : [...value, t])
            }
            sx={{ fontWeight: 600 }}
          />
        );
      })}
    </Stack>
  );
};

const BrandAutocomplete: FC<{
  value: string[];
  onChange: (v: string[]) => void;
}> = ({ value, onChange }) => {
  const { data } = useSWR(
    ["brands-v2-filter"],
    () => getBrands({ size: 200, sort: "name,asc" }) as any,
    { revalidateOnFocus: false }
  );
  const brands: TBrand[] = (data as any)?.content ?? (data as any) ?? [];
  return (
    <TextField
      select
      size="small"
      label="Marca"
      InputLabelProps={{ shrink: true }}
      SelectProps={{
        multiple: true,
        value,
        onChange: (e) => onChange(e.target.value as string[]),
        displayEmpty: true,
        renderValue: (selected) => {
          const arr = selected as string[];
          if (arr.length === 0) {
            return (
              <span style={{ opacity: 0.6 }}>Todas</span>
            );
          }
          return `${arr.length} seleccionada${arr.length === 1 ? "" : "s"}`;
        },
      }}
    >
      {brands.map((b) => (
        <MenuItem key={b.id} value={b.id}>
          {b.name}
        </MenuItem>
      ))}
    </TextField>
  );
};

const CategoryAutocomplete: FC<{
  value: string[];
  onChange: (v: string[]) => void;
}> = ({ value, onChange }) => {
  const { data } = useSWR(
    ["categories-v2-filter"],
    () => getCategories({ size: 200, sort: "name,asc" }) as any,
    { revalidateOnFocus: false }
  );
  const categories: TCategory[] = (data as any)?.content ?? (data as any) ?? [];
  return (
    <TextField
      select
      size="small"
      label="Categoría"
      InputLabelProps={{ shrink: true }}
      SelectProps={{
        multiple: true,
        value,
        onChange: (e) => onChange(e.target.value as string[]),
        displayEmpty: true,
        renderValue: (selected) => {
          const arr = selected as string[];
          if (arr.length === 0) {
            return (
              <span style={{ opacity: 0.6 }}>Todas</span>
            );
          }
          return `${arr.length} seleccionada${arr.length === 1 ? "" : "s"}`;
        },
      }}
    >
      {categories.map((c) => (
        <MenuItem key={c.id} value={c.id}>
          {c.name}
        </MenuItem>
      ))}
    </TextField>
  );
};
